import { toast } from 'sonner'
import { PageBody, PageHeader } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useActiveSessions, useRevokeSession } from '@/hooks/use-sessions'
import { initials } from '@/lib/initials'
import { describeUserAgent } from '@/lib/user-agent'

const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit'
})
const dateFormat = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' })

export function SessionsPage(): React.JSX.Element {
  const sessions = useActiveSessions()
  const revoke = useRevokeSession()

  return (
    <PageBody width="narrow">
      <PageHeader title="Sessions" />
      <p className="text-sm text-muted-foreground">
        Everyone signed in to c-sheet. Signing a session out ends it on that device. To keep someone
        out for good, also remove their assignment to the enterprise application in Microsoft Entra.
      </p>

      {sessions.error && <p className="text-sm text-destructive">{sessions.error.message}</p>}
      <div className="rounded-lg border bg-card">
        {sessions.isPending ? (
          <div className="flex flex-col gap-2 p-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : sessions.data?.length ? (
          <ul className="divide-y">
            {sessions.data.map((session) => (
              <li key={session.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
                  {initials(session.userName) || '?'}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{session.userName}</span>
                    <span className="truncate text-muted-foreground">{session.userEmail}</span>
                    {session.current && <Badge variant="secondary">This device</Badge>}
                  </p>
                  <p className="text-muted-foreground">
                    {describeUserAgent(session.userAgent)} · signed in{' '}
                    {dateTimeFormat.format(new Date(session.createdAt))} · expires{' '}
                    {dateFormat.format(new Date(session.expiresAt))}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={revoke.isPending}
                  onClick={() =>
                    revoke.mutate(session.id, {
                      onSuccess: () =>
                        toast(
                          session.current
                            ? 'You are signed out.'
                            : `${session.userName} is signed out on ${describeUserAgent(session.userAgent)}.`
                        ),
                      onError: (error) => toast.error(error.message)
                    })
                  }
                >
                  Sign out
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-8 text-center text-sm text-muted-foreground">
            No one is signed in with Microsoft right now.
          </p>
        )}
      </div>
    </PageBody>
  )
}
